import React from "react";
import { Toast } from "./Toast.jsx";

/* ToastProvider + useToast — the design-system notification provider.
   Replaces the notistack SnackbarProvider (REG-378 part 3, FR-014), preserving
   queueing, stacking (maxVisible), dismissal (manual + auto-timeout), and the
   success/error/info/warning variant semantics.

   Mount once near the app root:
     <ToastProvider maxVisible={6}>{app}</ToastProvider>
   Then anywhere:
     const { enqueue, dismiss } = useToast();
     enqueue({ tone: "success", title: "Saved", message: "…", duration: 5000 }); */

// notistack-style tone aliases so call sites migrate with minimal change.
const TONE_ALIAS = { success: "positive", error: "negative", info: "info", warning: "warning", default: "neutral" };

const ToastContext = React.createContext(null);

let nextId = 0;

export function ToastProvider({ children, maxVisible = 6, defaultDuration = 5000 }) {
  const [toasts, setToasts] = React.useState([]);
  const timers = React.useRef({});

  const dismiss = React.useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const enqueue = React.useCallback(
    (opts = {}) => {
      nextId += 1;
      const id = `toast-${nextId}`;
      const tone = TONE_ALIAS[opts.tone] || opts.tone || "neutral";
      const duration = opts.duration == null ? defaultDuration : opts.duration;
      setToasts((list) => {
        const next = [...list, { id, tone, title: opts.title, message: opts.message, action: opts.action }];
        // Stacking cap: drop the oldest when over the limit.
        return next.length > maxVisible ? next.slice(next.length - maxVisible) : next;
      });
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [defaultDuration, maxVisible, dismiss]
  );

  React.useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const value = React.useMemo(() => ({ enqueue, dismiss }), [enqueue, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          left: 16,
          bottom: 16,
          zIndex: 2000,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 10,
          maxWidth: "100%",
        }}
      >
        {toasts.map((t) => (
          <Toast
            key={t.id}
            tone={t.tone}
            title={t.title}
            message={t.message}
            action={t.action}
            onClose={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
}

export default ToastProvider;
